"use client"; // Need to check if the current route is active or not.

import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import styles from "./styles.module.scss";
import { cn } from "@utils/utils";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { formatSlugToTitleCase } from "@utils/stringFormatters";
import { DEFAULT_CITY } from "@constants/global";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { normalizeCityName } from "@utils/urlNormalization";

const getNavigationItems = function (cityName: string) {
  return [
    { label: "Overview", href: "", pageName:"MM page"},
    { label: "Mileage/Range", href: "mileage/", pageName: "Mileage/Range page"},
    { label: "Specs & Features", href: "specifications/", pageName:"Specs & Features page"},
    { label: "Price", href: `price-in-${normalizeCityName(cityName)}/`, pageName:"Price page"},
    { label: "Colours", href: "colours/", pageName:"Colours page"},
    { label: "Dimensions", href: "dimensions/", pageName:"Dimensions page"},
    { label: "Variants", href: "variants/", pageName:"Variants page"},
    { label: "Images", href: "images/", pageName:"Images page"},
    { label: "Review", href: "review/", pageName:"Review page"},
    { label: "FAQ", href: "faqs/", pageName:"FAQ page"},
  ];
};

export default function GlobalNavigationHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  const pathSegments = pathname?.split("/");
  const slug = pathSegments?.[2];
  const city = getSelectedCityClient();
  const cityName = city?.name || DEFAULT_CITY;
  const navigationItems = getNavigationItems(cityName);
  const makeName = slug ? formatSlugToTitleCase(slug.split("-")[0]) : "";
  const modelName = slug
    ? formatSlugToTitleCase(slug.split("-").slice(1).join("-"))
    : "";

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  // Scroll active item into view on mobile
  useEffect(() => {
    const activeItem = document.querySelector(
      `.${styles["navigation-header__item--active"]}`
    );
    if (activeItem && window.innerWidth <= 600) {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [pathname]);

  if ((pathSegments?.length ?? 0) <= 4) {
    return null;
  }

  const currentSisterPage = pathSegments?.[3];

  const isActiveRoute = (href: string) => {
    if (href === "" && pathSegments?.length === 3) {
      return true;
    }
    return `${currentSisterPage}/` === href;
  };

  return (
    <div className={styles["navigation-header-wrapper--visible"]}>
      <nav className={styles["navigation-header"]}>
        <ul className={styles["navigation-header__list"]}>
          {navigationItems.map((item) => (
            <li
              key={item.label}
              className={cn(
                styles["navigation-header__item"],
                isActiveRoute(item.href) &&
                  styles["navigation-header__item--active"]
              )}
            >
              <a
                href={`/${process.env.NEXT_PUBLIC_BASE_TERM}cars/${slug}${
                  item.href ? `/${item.href}` : "/"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                    pageType: item.pageName,
                    additionalProps: {
                      action_type: "tap_btn",
                      action_name: "tap_btn_mm_header",
                      selected_value: item.label,
                      vehicle_make: makeName,
                      vehicle_model: modelName,
                      city_name: cityName,
                    },
                  });
                  showLoader(`Loading ${makeName} ${modelName} ${item.label}`);
                  router.push(
                    `/cars/${slug}${item.href ? `/${item.href}` : ""}`
                  );
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
